import { coverDate } from "./quest-cover.ts";
import { lineDistanceM } from "./geo.ts";
import { parseRoutePlace, routePlaceLabel, type RoutePlace } from "./place-label.ts";
import { walkMinutes } from "./route-trace.ts";
import { matchSeoLocale, resolveSeoLocale, seoMessage, type SeoLocale } from "./quest-seo-messages.ts";

export type { SeoLocale };
export { matchSeoLocale, resolveSeoLocale, seoMessage };

export const MAPUS_ORIGIN = "https://mapus.app";
export const META_DESC_MAX = 160;

export interface QuestSeoFacts {
  id: string;
  title: string;
  description: string;
  author: string | null;
  city: string | null;
  country: string | null;
  routePlace: RoutePlace | null;
  routeLengthM: number;
  startFixed: boolean;
  timeLimitS: number | null;
  startTitle: string | null;
  finishTitle: string | null;
  status: string;
  createdAt: string;
  publishedAt: string | null;
  questType?: "route_trace" | "spot_trace" | null;
  spotCount?: number;
  estMinutesTotal?: number;
}

export function formatDistanceSeo(meters: number, lang: SeoLocale): string {
  if (meters < 1000) {
    const n = new Intl.NumberFormat(lang, { maximumFractionDigits: 0 }).format(Math.round(meters));
    return `${n} m`;
  }
  const n = new Intl.NumberFormat(lang, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(meters / 1000);
  return `${n} km`;
}

function formatWalkLabel(minutes: number, lang: SeoLocale): string {
  if (minutes < 60) {
    return lang === "ko" ? `${minutes}분` : `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (lang === "ko") return rest ? `${hours}시간 ${rest}분` : `${hours}시간`;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export function formatDateSeo(iso: string, lang: SeoLocale): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(lang, { dateStyle: "medium" }).format(d);
}

export function truncateMetaDescription(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length <= META_DESC_MAX) return trimmed;
  return `${trimmed.slice(0, META_DESC_MAX - 1).trimEnd()}…`;
}

export function questPageTitle(title: string, lang: SeoLocale): string {
  const trimmed = title.trim();
  return trimmed ? seoMessage(lang, "page.docTitle", { title: trimmed }) : seoMessage(lang, "page.docTitleEmpty");
}

export function questMetaDescriptionText(facts: QuestSeoFacts, lang: SeoLocale): string {
  const creator = facts.description.trim();
  if (creator) return truncateMetaDescription(creator);
  return truncateMetaDescription(questAutoSummary(facts, lang));
}

export function questAutoSummary(facts: QuestSeoFacts, lang: SeoLocale): string {
  const place =
    routePlaceLabel(facts.routePlace, lang) ||
    [facts.city, facts.country].filter(Boolean).join(", ") ||
    null;

  if (facts.questType === "spot_trace") {
    const count = facts.spotCount ?? 0;
    const title = facts.title.trim() || seoMessage(lang, "summary.untitled");
    const lead = place
      ? seoMessage(lang, "summary.placeSpots", { place, count })
      : seoMessage(lang, "summary.titleSpots", { title, count });
    const parts = [lead];
    if (facts.routeLengthM > 0) {
      parts.push(
        seoMessage(lang, "summary.walk", { time: formatWalkLabel(walkMinutes(facts.routeLengthM), lang) }),
      );
    }
    if (facts.estMinutesTotal && facts.estMinutesTotal > 0) {
      parts.push(seoMessage(lang, "summary.spotTime", { time: formatWalkLabel(facts.estMinutesTotal, lang) }));
    }
    if (facts.startFixed && facts.startTitle && facts.finishTitle) {
      parts.push(seoMessage(lang, "summary.endpoints", { start: facts.startTitle, finish: facts.finishTitle }));
    }
    const author = facts.author?.trim();
    if (author) parts.push(seoMessage(lang, "summary.byAuthor", { name: author }));
    const dateIso = coverDate({ status: facts.status, created_at: facts.createdAt, published_at: facts.publishedAt });
    const date = formatDateSeo(dateIso, lang);
    if (date && facts.status !== "draft") parts.push(seoMessage(lang, "summary.published", { date }));
    return parts.join(" ");
  }

  const distance = formatDistanceSeo(facts.routeLengthM, lang);
  const lead = place
    ? seoMessage(lang, "summary.placeRoute", { place, distance })
    : seoMessage(lang, "summary.titleRoute", {
        title: facts.title.trim() || seoMessage(lang, "summary.untitled"),
        distance,
      });

  const parts = [lead, seoMessage(lang, "summary.walk", { time: formatWalkLabel(walkMinutes(facts.routeLengthM), lang) })];

  if (facts.startFixed && facts.startTitle && facts.finishTitle) {
    parts.push(seoMessage(lang, "summary.endpoints", { start: facts.startTitle, finish: facts.finishTitle }));
  }

  const author = facts.author?.trim();
  if (author) parts.push(seoMessage(lang, "summary.byAuthor", { name: author }));

  const dateIso = coverDate({ status: facts.status, created_at: facts.createdAt, published_at: facts.publishedAt });
  const date = formatDateSeo(dateIso, lang);
  if (date && facts.status !== "draft") parts.push(seoMessage(lang, "summary.published", { date }));

  return parts.join(" ");
}

export function questExplainerHow(facts: Pick<QuestSeoFacts, "startFixed" | "timeLimitS">, lang: SeoLocale): string {
  const limitMin = facts.timeLimitS != null ? Math.round(facts.timeLimitS / 60) : null;
  if (facts.startFixed && limitMin) return seoMessage(lang, "explainer.howFixedLimit", { count: limitMin });
  if (facts.startFixed) return seoMessage(lang, "explainer.howFixedNoLimit");
  if (limitMin) return seoMessage(lang, "explainer.howAnywhereLimit", { count: limitMin });
  return seoMessage(lang, "explainer.howAnywhereNoLimit");
}

function questSpotExplainerHow(facts: Pick<QuestSeoFacts, "startFixed" | "timeLimitS">, lang: SeoLocale): string {
  const limitMin = facts.timeLimitS != null ? Math.round(facts.timeLimitS / 60) : null;
  if (facts.startFixed && limitMin) return seoMessage(lang, "explainer.spot.howFixedLimit", { count: limitMin });
  if (facts.startFixed) return seoMessage(lang, "explainer.spot.howFixedNoLimit");
  if (limitMin) return seoMessage(lang, "explainer.spot.howAnywhereLimit", { count: limitMin });
  return seoMessage(lang, "explainer.spot.howAnywhereNoLimit");
}

export function questExplainerBlocks(
  facts: Pick<QuestSeoFacts, "startFixed" | "timeLimitS" | "questType">,
  lang: SeoLocale,
): {
  title: string;
  what: string;
  how: string;
  tips: string;
} {
  if (facts.questType === "spot_trace") {
    return {
      title: seoMessage(lang, "explainer.spot.title"),
      what: seoMessage(lang, "explainer.spot.what"),
      how: questSpotExplainerHow(facts, lang),
      tips: seoMessage(lang, "explainer.spot.tips"),
    };
  }
  return {
    title: seoMessage(lang, "explainer.title"),
    what: seoMessage(lang, "explainer.what"),
    how: questExplainerHow(facts, lang),
    tips: seoMessage(lang, "explainer.tips"),
  };
}

export function parseRouteLineLength(routeLine: string | null): number | null {
  if (!routeLine) return null;
  try {
    const coords = JSON.parse(routeLine) as unknown;
    if (!Array.isArray(coords) || coords.length < 2) return null;
    return Math.round(lineDistanceM(coords as [number, number][]));
  } catch {
    return null;
  }
}

export function routePlaceFromRow(raw: string | null): RoutePlace | null {
  return parseRoutePlace(raw);
}
